import React from "react";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Warning,
  PlaceOutlined,
  InfoOutlined,
  VisibilityOutlined,
  DeleteOutline,
} from "@mui/icons-material";
import { Outlet, useNavigate, useLocation, Link as RouterLink } from "react-router-dom";

import OwnerForm from "../../../components/Forms/Owner";
import CarWashForm from "../../../components/Forms/CarWash";
import { DataTable, ColumnDef } from "../../../components/DataTable";
import DeleteCarWashDialog from "../Owner/screens/components/DeleteCarWashDialog";

import {
  getCarWashes,
  getAllPendingCarWashReviews,
  getMyChangeReviews,
  deleteCarWash,
} from "../../../services/carWash.service";
import { STATUS_SUCCESS } from "../../../constants/responseTypes";
import BasicModal from "./components/BasicModal";
import PendingCarWashReviewRow from "./components/PendingCarWashReviewRow";
import RMPendingReviewRow from "./components/RMPendingReviewRow";
import LocationMapModal from "./components/LocationMapModal";
import { IChangeReview } from "../../../types/review";
import { ICarWashObject } from "../../../types/carWash";
import { useRole } from "../../../context/RoleContext";
import { useAlert } from "../../../context/AlertContext";

// A row of the unified car-washes DataTable. `getCarWashes()` populates
// `owner` with "firstName lastName phone country_code" (mongoose always also
// returns `_id`, even though it isn't part of the `.select()` list) — the
// shared ICarWashObject interface doesn't declare `_id` on `owner`, so this
// row type widens it locally rather than editing the shared types file.
type CarWashRow = Omit<ICarWashObject, "owner"> & {
  owner?: {
    _id?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    country_code?: string;
  } | null;
};

const chipColor = (c?: string | null) =>
  c === "econom"
    ? ("success" as const)
    : c === "standard"
    ? ("info" as const)
    : c === "premium"
    ? ("warning" as const)
    : ("default" as const);

// Location cell — copied trigger pattern from the now-orphaned
// RMWashingCenterTableRow.tsx:39-51. Each cell owns its own modal-open state.
function LocationCell({ row }: { row: CarWashRow }) {
  const { t } = useTranslation();
  const [mapOpen, setMapOpen] = React.useState(false);
  const hasCoords =
    row.location?.latitude != null && row.location?.longitude != null;

  if (!hasCoords) {
    return <span>—</span>;
  }

  return (
    <>
      <Tooltip title={t("common.viewOnMap")}>
        <IconButton size="small" onClick={() => setMapOpen(true)}>
          <PlaceOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <LocationMapModal
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        latitude={row.location?.latitude}
        longitude={row.location?.longitude}
        title={row.legalName}
        address={row.location?.street}
      />
    </>
  );
}

// Wash-class tooltip popup — lifted as-is from the now-orphaned
// TableRowItem.tsx:54-101, not redesigned.
function WashClassCell({ row }: { row: CarWashRow }) {
  const { t } = useTranslation();
  if (!row.washClasses || row.washClasses.length === 0) {
    return <span>—</span>;
  }
  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ p: 0.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "inherit", borderBottom: "1px solid rgba(255,255,255,0.2)" }}>
                  {t("manageCarWashes.vehicle")}
                </TableCell>
                <TableCell sx={{ color: "inherit", borderBottom: "1px solid rgba(255,255,255,0.2)" }}>
                  {t("manageCarWashes.service")}
                </TableCell>
                <TableCell sx={{ color: "inherit", borderBottom: "1px solid rgba(255,255,255,0.2)" }}>
                  {t("manageCarWashes.class")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {row.washClasses
                .filter((e) => e.washClass !== null)
                .map((e, idx) => (
                  <TableRow key={`${e.vehicleType}-${e.serviceType}-${idx}`}>
                    <TableCell sx={{ color: "inherit", borderBottom: "none" }}>
                      {e.vehicleType}
                    </TableCell>
                    <TableCell sx={{ color: "inherit", borderBottom: "none" }}>
                      {e.serviceType}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "none" }}>
                      <Chip label={e.washClass} color={chipColor(e.washClass)} size="small" />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Box>
      }
    >
      <IconButton size="small" onClick={(ev) => ev.stopPropagation()}>
        <InfoOutlined fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// ONE screen for both SuperAdmin and RM. The unified "existing car washes"
// table is fed by getCarWashes() for both roles — the backend already scopes
// the result set to the caller's owners when the caller is a
// relationship_manager (ukar-backend/src/controllers/adminController.js
// getCarWashes), so no role branch is needed for this table or its columns.
//
// The separate "pending car washes" table (brand-new car-wash creation
// requests, ChangeReviews with carWash: null) still requires a role branch —
// NOT because of this refactor, but because the backend exposes two
// permission-restricted endpoints: GET /admins/changeReviews is
// restrictTo("super_admin") and GET /admins/rm/changeReviews is
// restrictTo("relationship_manager"); calling the wrong one 403s. That branch
// lives at the data-fetch/row-renderer level, never inside the DataTable.
// ---------------------------------------------------------------------------
export default function ManageCarWashes() {
  const { t } = useTranslation();
  const { isRelationshipManager } = useRole();
  const navigate = useNavigate();
  const location = useLocation();
  const { setAlert } = useAlert();
  const incomingFilter = (location.state as { filter?: string } | null)?.filter; // "pending" | undefined
  const [activeFilter, setActiveFilter] = React.useState<string | undefined>(
    incomingFilter
  );
  const pendingTableRef = React.useRef<HTMLDivElement>(null);

  const [visibleModal, setVisibleModal] = React.useState("");
  const [ownerID, setOwnerID] = React.useState("");
  const [carWashes, setCarWashes] = React.useState<CarWashRow[]>([]);
  const [pendingReviews, setPendingReviews] = React.useState<IChangeReview[]>(
    []
  );
  const [search, setSearch] = React.useState("");

  // Delete-car-wash flow — reuses DeleteCarWashDialog and follows the same
  // state shape / handler flow as WashingCenters.tsx (owner-scoped screen).
  // Visible to both roles: the backend (deleteCarWash controller) already
  // scopes an RM to their own owners and 404s otherwise, so no role check is
  // needed here.
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [carWashToDelete, setCarWashToDelete] =
    React.useState<CarWashRow | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);

  // Separate pagination for the Pending Car Washes table (the unified
  // DataTable above owns its own pagination internally).
  const [pendingPage, setPendingPage] = React.useState(0);
  const [pendingRowsPerPage, setPendingRowsPerPage] = React.useState(5);

  const handleGetCarWashes = React.useCallback(async () => {
    const res = await getCarWashes();
    if (res.status === STATUS_SUCCESS) {
      setCarWashes(res.data as unknown as CarWashRow[]);
    }
  }, []);

  const fetchPendingReviews = React.useCallback(async () => {
    try {
      const data = isRelationshipManager
        ? await getMyChangeReviews()
        : await getAllPendingCarWashReviews();
      setPendingReviews(data ?? []);
    } catch (err: any) {
      setAlert(
        "error",
        err?.message || t("serviceErrors.fetchPendingCarWashReviews")
      );
    }
  }, [isRelationshipManager, setAlert, t]);

  const refreshBoth = React.useCallback(() => {
    handleGetCarWashes();
    fetchPendingReviews();
  }, [handleGetCarWashes, fetchPendingReviews]);

  React.useEffect(() => {
    refreshBoth();
  }, [refreshBoth]);

  const handleCloseModal = () => {
    handleGetCarWashes();
    setVisibleModal("");
  };

  const handleOwnerSubmit = (submittedOwnerID: string) => {
    setOwnerID(submittedOwnerID);
    setVisibleModal("carWash");
  };

  const handleChangePendingPage = (event: unknown, newPage: number) => {
    setPendingPage(newPage);
  };

  const handleChangePendingRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPendingRowsPerPage(parseInt(event.target.value, 10));
    setPendingPage(0);
  };

  // Search filters the rows passed to DataTable — DataTable itself never
  // filters.
  const filteredCarWashes = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return carWashes;
    return carWashes.filter((cw) =>
      (cw.legalName || "").toLowerCase().includes(term)
    );
  }, [carWashes, search]);

  const visiblePendingRows = React.useMemo(
    () =>
      [...pendingReviews].slice(
        pendingPage * pendingRowsPerPage,
        pendingPage * pendingRowsPerPage + pendingRowsPerPage
      ),
    [pendingPage, pendingRowsPerPage, pendingReviews]
  );

  // Scroll the pending table into view when arriving with the "pending" filter.
  React.useEffect(() => {
    if (activeFilter === "pending" && pendingReviews.length > 0) {
      pendingTableRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeFilter, pendingReviews.length]);

  const goToDetail = React.useCallback(
    (id: string) => navigate(`${id}/info`),
    [navigate]
  );

  const handleDeleteClick = React.useCallback((carWash: CarWashRow) => {
    setCarWashToDelete(carWash);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = React.useCallback(async () => {
    if (!carWashToDelete?._id) return;

    setIsDeleting(true);
    try {
      await deleteCarWash(carWashToDelete._id);
      await handleGetCarWashes();
      setDeleteModalOpen(false);
      setCarWashToDelete(null);
    } catch (error: any) {
      setAlert("error", error?.message || t("serviceErrors.deleteCarWash"));
    } finally {
      setIsDeleting(false);
    }
  }, [carWashToDelete, handleGetCarWashes, setAlert, t]);

  const handleDeleteCancel = React.useCallback(() => {
    setDeleteModalOpen(false);
    setCarWashToDelete(null);
  }, []);

  const columns: ColumnDef<CarWashRow>[] = React.useMemo(
    () => [
      {
        id: "legalName",
        labelKey: "common.legalName",
        priority: "essential",
        sortable: true,
        render: (row) => row.legalName,
      },
      {
        id: "location",
        labelKey: "manageCarWashes.colLocation",
        priority: "high",
        sortable: false,
        stopRowClick: true,
        render: (row) => <LocationCell row={row} />,
      },
      {
        id: "address",
        labelKey: "manageCarWashes.colAddress",
        priority: "low",
        sortable: true,
        sortValue: (row) => row.location?.street ?? "",
        render: (row) => {
          const street = row.location?.street || "—";
          return (
            <Tooltip title={street}>
              <Typography noWrap sx={{ maxWidth: 200 }}>
                {street}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        id: "contactPerson",
        labelKey: "manageCarWashes.colContactPerson",
        priority: "high",
        sortable: true,
        stopRowClick: true,
        sortValue: (row) =>
          `${row.owner?.firstName ?? ""} ${row.owner?.lastName ?? ""}`.trim(),
        render: (row) => {
          const name = `${row.owner?.firstName ?? ""} ${row.owner?.lastName ?? ""}`.trim();
          if (!name || !row.owner?._id) return <span>—</span>;
          return (
            <RouterLink to={`/dashboard/manage-owners/${row.owner._id}/info`}>
              {name}
            </RouterLink>
          );
        },
      },
      {
        id: "washClass",
        labelKey: "manageCarWashes.colWashingClass",
        priority: "low",
        sortable: false,
        render: (row) => <WashClassCell row={row} />,
      },
      {
        id: "pendingReviews",
        labelKey: "manageCarWashes.colPendingReviews",
        priority: "high",
        sortable: true,
        align: "right",
        sortValue: (row) => row.pendingReviewCount ?? 0,
        render: (row) => row.pendingReviewCount ?? 0,
      },
      {
        id: "rating",
        labelKey: "manageCarWashes.colRating",
        priority: "low",
        sortable: true,
        align: "right",
        sortValue: (row) => row.rating ?? 0,
        render: (row) => row.rating ?? 0,
      },
      {
        id: "actions",
        labelKey: "common.actions",
        priority: "essential",
        sortable: false,
        stopRowClick: true,
        render: (row) => (
          <>
            <Tooltip title={t("common.view")}>
              <IconButton size="small" onClick={() => goToDetail(row._id ?? "")}>
                <VisibilityOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("common.delete")}>
              <IconButton size="small" onClick={() => handleDeleteClick(row)}>
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ),
      },
    ],
    [t, goToDetail, handleDeleteClick]
  );

  return (
    <Box className="w-full">
      <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 my-5 shadow-md w-full p-2 sm:p-4 rounded">
        <Box className="flex flex-row sm:flex-row justify-between gap-4 w-full ">
          <Typography variant="h6" className="font-bold">
            {t("carWashes.title")}
          </Typography>

          <BasicModal setVisibleModal={setVisibleModal} />

          {visibleModal === "owner" && (
            <OwnerForm
              cb={handleOwnerSubmit}
              isOpen={true}
              closeModal={handleCloseModal}
            />
          )}

          {visibleModal === "carWash" && (
            <CarWashForm
              ownerID={ownerID}
              isOpen={true}
              closeModal={handleCloseModal}
            />
          )}
        </Box>

        <Box className="w-full sm:w-auto flex justify-start sm:justify-end">
          <TextField
            size="small"
            className="w-full sm:w-80"
            label={t("carWashes.searchCarWash")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="search"
          />
        </Box>
      </Box>

      {activeFilter && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={t("carWashes.filter", { value: activeFilter })}
            color="primary"
            onDelete={() => setActiveFilter(undefined)}
          />
        </Box>
      )}

      {/* Existing car washes — the unified DataTable, fed by getCarWashes(),
          already scoped server-side per role. */}
      <Box sx={{ width: "100%", mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            {t("manageCarWashes.approvedCarWashes")}
          </Typography>
          <Chip
            label={t("carWashes.carWashesCount", {
              count: filteredCarWashes.length,
            })}
            color="success"
            size="small"
          />
        </Box>
        <Box className="w-full overflow-x-auto">
          <DataTable<CarWashRow>
            tableId="carWashes"
            columns={columns}
            rows={filteredCarWashes}
            getRowId={(row) => row._id ?? ""}
            onRowClick={goToDetail}
            emptyLabelKey="manageCarWashes.noApprovedCarWashes"
            initialOrderBy="legalName"
          />
        </Box>
      </Box>

      <DeleteCarWashDialog
        open={deleteModalOpen}
        carWash={carWashToDelete as unknown as ICarWashObject | null}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={isDeleting}
      />

      <Divider sx={{ my: 4 }} />

      {/* Pending Car Washes Table — brand-new car-wash creation requests,
          different dataset (ChangeReviews with carWash: null). Kept
          hand-rolled, per role (see role-branch note above the component). */}
      <Box ref={pendingTableRef} sx={{ width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            {t("dashboard.pendingCarWashes")}
          </Typography>
          <Chip
            icon={<Warning />}
            label={t("carWashes.pendingCount", {
              count: pendingReviews.length,
            })}
            color="warning"
            size="small"
          />
        </Box>
        <Paper className="w-full mb-2">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ textAlign: "left", padding: "12px 16px" }}>
                    {t("manageCarWashes.colLabelName")}
                  </TableCell>
                  <TableCell style={{ textAlign: "left", padding: "12px 16px" }}>
                    {t("manageCarWashes.colOwner")}
                  </TableCell>
                  <TableCell style={{ textAlign: "left", padding: "12px 16px" }}>
                    {t("manageCarWashes.colCountdown")}
                  </TableCell>
                  <TableCell style={{ textAlign: "left", padding: "12px 16px" }}>
                    {t("common.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visiblePendingRows.length > 0 ? (
                  visiblePendingRows.map((review) =>
                    isRelationshipManager ? (
                      <RMPendingReviewRow
                        key={review._id}
                        item={review}
                        onActionComplete={refreshBoth}
                        onError={(msg) => setAlert("error", msg)}
                      />
                    ) : (
                      <PendingCarWashReviewRow
                        key={review._id}
                        item={review}
                        onActionComplete={refreshBoth}
                        onError={(msg) => setAlert("error", msg)}
                      />
                    )
                  )
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      <Typography color="text.secondary">
                        {t("manageCarWashes.noPendingCarWashRequests")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={pendingReviews.length}
            rowsPerPage={pendingRowsPerPage}
            page={pendingPage}
            onPageChange={handleChangePendingPage}
            onRowsPerPageChange={handleChangePendingRowsPerPage}
          />
        </Paper>
      </Box>
      <Outlet />
    </Box>
  );
}
